import {ChangeDetectionStrategy, Component, computed, input, output, signal} from "@angular/core"
import {NgTemplateOutlet} from "@angular/common"
import type {CurveLikeNode, NumberLikeNode, MaterialLikeNode, CurveExport, MaterialExport, NumberExport} from "@cm/template-nodes"
import type {
    MattressComponentInstance,
    MattressComponentGraphGenerator,
    MattressComponentInterfaceDescriptor,
    MattressInterfaceDescriptor,
    MattressCurveLikeNode,
    MattressReferenceFrameLikeNode,
    MattressCurveExport,
    MattressReferenceFrameExport,
} from "@cm/mattress-nodes"
import {
    InterfaceDescriptor,
    CurveInfo,
    MaterialInfo,
    NumberInfo,
    TemplateInfo,
    ImageInfo,
    MeshInfo,
    StringInfo,
    ConfigInfo,
    TransformInfo,
} from "@cm/template-nodes"
import {MattressCurveInfo, MattressReferenceFrameInfo, MATTRESS_BASE_MESH, unwrapPrefixFromInterfaceId} from "@cm/mattress-nodes"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, type SelectionPossibilities} from "@template-editor/components/value-slot/value-slot.component"
import {ListItemComponent} from "@app/common/components/item/list-item/list-item.component"
import {MatTooltipModule} from "@angular/material/tooltip"

import type {z} from "zod/v4-mini"

export function selectionPossibilitiesForDescriptor(descriptor: MattressInterfaceDescriptor) {
    if (descriptor instanceof MeshInfo) return [{name: "Mattress Base Mesh", value: MATTRESS_BASE_MESH}]
    if (descriptor instanceof ConfigInfo)
        return descriptor.props.variants.map((variant) => ({
            name: variant.name,
            value: variant.id,
        }))
    else return null
}

type ParameterGroup<T> = {
    label: string
    children: ParameterGroup<T>[]
    items: T[]
}

function buildParameterGroups<T extends {unwrappedId: string}>(items: T[]): ParameterGroup<T> {
    const root: ParameterGroup<T> = {label: "", children: [], items: []}
    for (const item of items) {
        const segments = item.unwrappedId.split("/")
        const groupSegments = segments.slice(0, -1)
        let current = root
        for (const segment of groupSegments) {
            let child = current.children.find((c) => c.label === segment)
            if (!child) {
                child = {label: segment, children: [], items: []}
                current.children.push(child)
            }
            current = child
        }
        current.items.push(item)
    }
    return root
}

type MattressComponentInput = {
    unwrappedId: string
    label: string
    selectionPossibilities:
        | SelectionPossibilities<MattressCurveLikeNode | MattressCurveExport | null>
        | SelectionPossibilities<MattressReferenceFrameLikeNode | MattressReferenceFrameExport | null>
        | SelectionPossibilities<NumberLikeNode | NumberExport | null>
        | SelectionPossibilities<MaterialLikeNode | MaterialExport | null>
        | SelectionPossibilities<CurveLikeNode | CurveExport | null>
        | SelectionPossibilities<TemplateInfo | null>
        | SelectionPossibilities<ImageInfo | null>
        | SelectionPossibilities<MeshInfo | null>
        | SelectionPossibilities<StringInfo | null>
        | SelectionPossibilities<ConfigInfo | null>
        | SelectionPossibilities<TransformInfo | null>
        | null
    overwrittenValue: unknown
    schema?: z.ZodMiniType<unknown, unknown>
    descriptor: MattressInterfaceDescriptor
}

type MattressComponentOutput = {
    unwrappedId: string
    label: string
    descriptor: MattressInterfaceDescriptor
}

export interface MattressInputParameterProvider {
    (
        key: string,
        descriptor: MattressCurveInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<MattressCurveLikeNode | MattressCurveExport | null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
    (
        key: string,
        descriptor: MattressReferenceFrameInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<MattressReferenceFrameLikeNode | MattressReferenceFrameExport | null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
    (
        key: string,
        descriptor: NumberInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<NumberLikeNode | NumberExport | null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
    (
        key: string,
        descriptor: MaterialInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<MaterialLikeNode | MaterialExport | null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
    (
        key: string,
        descriptor: CurveInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<CurveLikeNode | CurveExport | null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
    (
        key: string,
        descriptor: TemplateInfo,
    ): {selectionPossibilities: SelectionPossibilities<TemplateInfo | null> | null; overwrittenValue: unknown; schema?: z.ZodMiniType<unknown, unknown>}
    (
        key: string,
        descriptor: ImageInfo,
    ): {selectionPossibilities: SelectionPossibilities<ImageInfo | null> | null; overwrittenValue: unknown; schema?: z.ZodMiniType<unknown, unknown>}
    (
        key: string,
        descriptor: MeshInfo,
    ): {selectionPossibilities: SelectionPossibilities<MeshInfo | null> | null; overwrittenValue: unknown; schema?: z.ZodMiniType<unknown, unknown>}
    (
        key: string,
        descriptor: StringInfo,
    ): {selectionPossibilities: SelectionPossibilities<StringInfo | null> | null; overwrittenValue: unknown; schema?: z.ZodMiniType<unknown, unknown>}
    (
        key: string,
        descriptor: ConfigInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
    (
        key: string,
        descriptor: TransformInfo,
    ): {
        selectionPossibilities: SelectionPossibilities<null> | null
        overwrittenValue: unknown
        schema?: z.ZodMiniType<unknown, unknown>
    }
}

export type MattressFabricStackInterfaceInspectorNode = MattressComponentInstance | MattressComponentGraphGenerator

@Component({
    selector: "cm-mattress-component-instance-interface-inspector",
    templateUrl: "./mattress-component-instance-interface-inspector.component.html",
    styleUrl: "./mattress-component-instance-interface-inspector.component.scss",
    imports: [InspectorSectionComponent, ValueSlotComponent, MatTooltipModule, NgTemplateOutlet, ListItemComponent],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MattressComponentInstanceInterfaceInspectorComponent {
    readonly $componentDescriptors = input.required<MattressComponentInterfaceDescriptor<unknown>[]>({alias: "componentDescriptors"})
    readonly $templateDescriptors = input.required<InterfaceDescriptor<unknown>[]>({alias: "templateDescriptors"})
    readonly $node = input<MattressFabricStackInterfaceInspectorNode | undefined>(undefined, {alias: "node"})
    readonly $inputParameterProvider = input<MattressInputParameterProvider | undefined>(undefined, {alias: "inputParameterProvider"})
    readonly $wrappedInstanceId = input<string | undefined>(undefined, {alias: "wrappedInstanceId"})

    readonly updateComponentInstanceParameter = output<{key: string; value: unknown}>()
    readonly componentInstanceOutputItemClicked = output<MattressInterfaceDescriptor>()

    readonly $inputs = computed<MattressComponentInput[]>(() => {
        const provider = this.$inputParameterProvider()

        const fallback = (_key: string) => ({selectionPossibilities: null, overwrittenValue: undefined, schema: undefined})

        const wrappedInstanceId = this.$wrappedInstanceId()

        const componentInputs = this.$componentDescriptors()
            .filter((descriptor) => descriptor.props.type === "input")
            .map<MattressComponentInput>((descriptor) => {
                const unwrappedId = wrappedInstanceId ? unwrapPrefixFromInterfaceId(wrappedInstanceId, descriptor.props.id) : descriptor.props.id
                if (descriptor instanceof MattressCurveInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof MattressReferenceFrameInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else throw new Error("Unsupported descriptor type")
            })

        const templateInputs = this.$templateDescriptors()
            .filter((descriptor) => descriptor.props.type === "input")
            .map<MattressComponentInput>((descriptor) => {
                const unwrappedId = wrappedInstanceId ? unwrapPrefixFromInterfaceId(wrappedInstanceId, descriptor.props.id) : descriptor.props.id
                if (descriptor instanceof NumberInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof MaterialInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof CurveInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof TemplateInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof ImageInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof MeshInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof StringInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof ConfigInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else if (descriptor instanceof TransformInfo)
                    return {unwrappedId, label: descriptor.props.name, ...(provider ? provider(unwrappedId, descriptor) : fallback(unwrappedId)), descriptor}
                else throw new Error("Unsupported descriptor type")
            })

        return [...componentInputs, ...templateInputs]
    })

    readonly $outputs = computed<MattressComponentOutput[]>(() => {
        const wrappedInstanceId = this.$wrappedInstanceId()

        const componentOutputs = this.$componentDescriptors()
            .filter((descriptor) => descriptor.props.type === "output")
            .map<MattressComponentOutput>((descriptor) => {
                const unwrappedId = wrappedInstanceId ? unwrapPrefixFromInterfaceId(wrappedInstanceId, descriptor.props.id) : descriptor.props.id
                if (descriptor instanceof MattressCurveInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                else if (descriptor instanceof MattressReferenceFrameInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                throw new Error("Unsupported descriptor type")
            })

        const templateOutputs = this.$templateDescriptors()
            .filter((descriptor) => descriptor.props.type === "output")
            .map<MattressComponentOutput>((descriptor) => {
                const unwrappedId = wrappedInstanceId ? unwrapPrefixFromInterfaceId(wrappedInstanceId, descriptor.props.id) : descriptor.props.id
                if (descriptor instanceof NumberInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                else if (descriptor instanceof MaterialInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                else if (descriptor instanceof CurveInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                else if (descriptor instanceof StringInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                else if (descriptor instanceof TransformInfo) return {unwrappedId, label: descriptor.props.name, descriptor}
                else throw new Error("Unsupported descriptor type")
            })

        return [...componentOutputs, ...templateOutputs]
    })

    readonly $groupedInputs = computed(() => buildParameterGroups(this.$inputs()))
    readonly $groupedOutputs = computed(() => buildParameterGroups(this.$outputs()))

    readonly $expandedInputGroups = signal(new Set<string>())
    readonly $expandedOutputGroups = signal(new Set<string>())

    isInputGroupExpanded(path: string): boolean {
        return this.$expandedInputGroups().has(path)
    }

    isOutputGroupExpanded(path: string): boolean {
        return this.$expandedOutputGroups().has(path)
    }

    toggleInputGroup(path: string) {
        this.$expandedInputGroups.update((expanded) => {
            const next = new Set(expanded)
            if (next.has(path)) next.delete(path)
            else next.add(path)
            return next
        })
    }

    toggleOutputGroup(path: string) {
        this.$expandedOutputGroups.update((expanded) => {
            const next = new Set(expanded)
            if (next.has(path)) next.delete(path)
            else next.add(path)
            return next
        })
    }

    onUpdateComponentInstanceParameter(key: string, value: unknown) {
        this.updateComponentInstanceParameter.emit({key, value})
    }

    onComponentInstanceOutputItemClicked(descriptor: MattressInterfaceDescriptor) {
        this.componentInstanceOutputItemClicked.emit(descriptor)
    }
}
